import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    password: "",
    phone: "",
    state: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const payload = {
      email: form.email.trim(),
      password: form.password,
      name: form.name.trim(),
      business_name: form.business_name.trim() || null,
      phone: form.phone.trim(),
      state: form.state || null,
    };
    const res = await signup(payload);
    setLoading(false);
    if (res.ok) {
      const params = new URLSearchParams();
      if (res.data.dev_activation_token) {
        params.set("token", res.data.dev_activation_token);
      }
      params.set("email", payload.email);
      navigate(`/activate?${params.toString()}`, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthLayout testid="signup-page" quote="Two minutes to set up. Zero contracts. All Australian.">
      <div className="gt-label mb-3">Create your organiser account</div>
      <h2 className="font-heading text-4xl font-black tracking-tighter mb-2">Start selling tickets</h2>
      <p className="text-[var(--gt-ink-2)] text-sm mb-8">
        Already have an account?{" "}
        <Link to="/login" className="text-[var(--gt-ink)] font-semibold underline underline-offset-4 hover:text-[var(--gt-accent)]" data-testid="link-to-login">
          Log in
        </Link>
      </p>

      <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="signup-form">
        <div>
          <label className="gt-form-label" htmlFor="name">Your name</label>
          <input
            id="name"
            className="gt-input"
            value={form.name}
            onChange={onChange("name")}
            placeholder="Jane Kelly"
            required
            data-testid="signup-name-input"
          />
        </div>
        <div>
          <label className="gt-form-label" htmlFor="business_name">Business or event brand <span className="text-[var(--gt-ink-3)] font-normal">(optional)</span></label>
          <input
            id="business_name"
            className="gt-input"
            value={form.business_name}
            onChange={onChange("business_name")}
            placeholder="Bondi Live Presents"
            data-testid="signup-business-input"
          />
        </div>
        <div>
          <label className="gt-form-label" htmlFor="email">Work email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="gt-input"
            value={form.email}
            onChange={onChange("email")}
            placeholder="you@yourvenue.com.au"
            required
            data-testid="signup-email-input"
          />
        </div>
        <div>
          <label className="gt-form-label" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="gt-input"
            value={form.password}
            onChange={onChange("password")}
            placeholder="Minimum 8 characters"
            minLength={8}
            required
            data-testid="signup-password-input"
          />
        </div>
        <div>
          <label className="gt-form-label" htmlFor="phone">Australian phone number</label>
          <input
            id="phone"
            type="tel"
            className="gt-input"
            value={form.phone}
            onChange={onChange("phone")}
            placeholder="0412 345 678"
            autoComplete="tel"
            inputMode="tel"
            pattern="^(\+?61|0)[\s\-]?[2-9](?:[\s\-]?\d){8}$"
            title="Enter an Australian mobile or landline (e.g. 0412 345 678 or +61 412 345 678)."
            required
            data-testid="signup-phone-input"
          />
          <p className="mt-1 text-xs text-[var(--gt-ink-3)]">Australian numbers only. Used for urgent event communication.</p>
        </div>
        <div>
          <label className="gt-form-label" htmlFor="state">State</label>
          <select
            id="state"
            className="gt-input"
            value={form.state}
            onChange={onChange("state")}
            data-testid="signup-state-select"
          >
            <option value="">Select a state</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {error && (
          <div className="text-sm text-[var(--gt-accent)] font-medium" data-testid="signup-error">
            {error}
          </div>
        )}

        <button type="submit" className="gt-btn-primary mt-2" disabled={loading} data-testid="signup-submit-btn">
          {loading && <Loader2 size={16} className="animate-spin" />}
          Create account
        </button>

        <p className="text-xs text-[var(--gt-ink-3)] mt-2 leading-relaxed">
          By continuing you agree to the Getickets Terms and Privacy Policy. GST-registered
          organisers can add their ABN later in Payouts.
        </p>
      </form>
    </AuthLayout>
  );
}
